<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';

const { t } = useI18n();

const ENGAGED_SUFFIX = '_engajou_24h';
const NOT_ENGAGED_SUFFIX = '_nao_engajou_24h';
const DROPDOWN_CLOSE_DELAY_MS = 150;

const phonesText = ref('');
const prefix = ref('');
const isLoading = ref(false);
const result = ref(null);

const inboxesList = useMapGetter('inboxes/getInboxes');
const allLabels = useMapGetter('labels/getLabels');

const selectedInboxId = ref(null);
const excludedLabelTitles = ref([]);
const labelSearch = ref('');
const showLabelDropdown = ref(false);

onMounted(() => {
  if (inboxesList.value?.length && !selectedInboxId.value) {
    selectedInboxId.value = inboxesList.value[0].id;
  }
});

const parsePhones = text => {
  return text
    .split(/[\n,;]+/)
    .map(p => p.trim())
    .filter(Boolean);
};

const phonesCount = computed(() => parsePhones(phonesText.value).length);

const previewEngagedLabel = computed(() =>
  prefix.value ? `${prefix.value}${ENGAGED_SUFFIX}` : ENGAGED_SUFFIX
);

const previewNotEngagedLabel = computed(() =>
  prefix.value ? `${prefix.value}${NOT_ENGAGED_SUFFIX}` : NOT_ENGAGED_SUFFIX
);

const filteredLabels = computed(() => {
  const search = labelSearch.value.toLowerCase();
  return (allLabels.value || []).filter(
    l =>
      !excludedLabelTitles.value.includes(l.title) &&
      l.title.toLowerCase().includes(search)
  );
});

const toggleLabel = title => {
  const idx = excludedLabelTitles.value.indexOf(title);
  if (idx === -1) {
    excludedLabelTitles.value = [...excludedLabelTitles.value, title];
  } else {
    excludedLabelTitles.value = excludedLabelTitles.value.filter(
      l => l !== title
    );
  }
  labelSearch.value = '';
  showLabelDropdown.value = false;
};

const removeLabel = title => {
  excludedLabelTitles.value = excludedLabelTitles.value.filter(
    l => l !== title
  );
};

const applyLabels = async () => {
  if (isLoading.value) return;

  const excludedPhones = parsePhones(phonesText.value);

  isLoading.value = true;
  result.value = null;
  try {
    const { data } = await ContactAPI.applyEngagementLabels({
      excludedPhones,
      excludedLabels: excludedLabelTitles.value,
      prefix: prefix.value || 'contatos',
      inboxId: selectedInboxId.value || null,
    });
    result.value = data;
    useAlert(t('CONTACTS_ENGAGEMENT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_ENGAGEMENT.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-surface-1">
    <div class="flex flex-col gap-6 p-6 max-w-2xl">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('CONTACTS_ENGAGEMENT.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CONTACTS_ENGAGEMENT.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_ENGAGEMENT.PREFIX_LABEL') }}
          </label>
          <p class="text-xs text-n-slate-10">
            {{ t('CONTACTS_ENGAGEMENT.PREFIX_HELP') }}
          </p>
          <input
            v-model="prefix"
            type="text"
            :placeholder="t('CONTACTS_ENGAGEMENT.PREFIX_PLACEHOLDER')"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
          <div class="flex flex-col gap-1 mt-1">
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <span class="size-2 rounded-full bg-green-500 shrink-0" />
              <span class="font-mono">{{ previewEngagedLabel }}</span>
              <span class="text-n-slate-9 italic">
                {{ t('CONTACTS_ENGAGEMENT.SUFFIX_NOTE') }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <span class="size-2 rounded-full bg-red-500 shrink-0" />
              <span class="font-mono">{{ previewNotEngagedLabel }}</span>
              <span class="text-n-slate-9 italic">
                {{ t('CONTACTS_ENGAGEMENT.SUFFIX_NOTE') }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label for="engagement-inbox-select" class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_ENGAGEMENT.INBOX_LABEL') }}
          </label>
          <p id="engagement-inbox-help" class="text-xs text-n-slate-10">
            {{ t('CONTACTS_ENGAGEMENT.INBOX_HELP') }}
          </p>
          <select
            id="engagement-inbox-select"
            v-model="selectedInboxId"
            aria-describedby="engagement-inbox-help"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option :value="null">
              {{ t('CONTACTS_ENGAGEMENT.INBOX_ALL') }}
            </option>
            <option
              v-for="inbox in inboxesList"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Labels exclusion selector -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_ENGAGEMENT.EXCLUDED_LABELS_LABEL') }}
          </label>
          <p class="text-xs text-n-slate-10">
            {{ t('CONTACTS_ENGAGEMENT.EXCLUDED_LABELS_HELP') }}
          </p>

          <!-- Selected labels chips -->
          <div
            v-if="excludedLabelTitles.length > 0"
            class="flex flex-wrap gap-1.5 mb-1"
          >
            <span
              v-for="title in excludedLabelTitles"
              :key="title"
              class="inline-flex items-center gap-1 rounded-full bg-n-alpha-3 px-2.5 py-0.5 text-xs font-medium text-n-slate-12"
            >
              {{ title }}
              <button
                type="button"
                class="flex items-center text-n-slate-9 hover:text-n-slate-12 focus:outline-none"
                @click="removeLabel(title)"
              >
                <span class="i-lucide-x size-3" />
              </button>
            </span>
          </div>

          <!-- Dropdown trigger -->
          <div class="relative">
            <input
              v-model="labelSearch"
              type="text"
              :placeholder="t('CONTACTS_ENGAGEMENT.EXCLUDED_LABELS_PLACEHOLDER')"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
              @focus="showLabelDropdown = true"
              @blur="
                () =>
                  setTimeout(() => {
                    showLabelDropdown = false;
                  }, DROPDOWN_CLOSE_DELAY_MS)
              "
            />
            <ul
              v-if="showLabelDropdown && filteredLabels.length > 0"
              class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-md py-1"
            >
              <li
                v-for="label in filteredLabels"
                :key="label.title"
                class="flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 cursor-pointer hover:bg-n-alpha-2"
                @mousedown.prevent="toggleLabel(label.title)"
              >
                <span
                  class="size-2.5 rounded-full shrink-0"
                  :style="{ backgroundColor: label.color }"
                />
                {{ label.title }}
              </li>
            </ul>
          </div>
          <p class="text-xs text-n-slate-10">
            {{
              t('CONTACTS_ENGAGEMENT.LABELS_COUNT', {
                count: excludedLabelTitles.length,
              })
            }}
          </p>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_ENGAGEMENT.EXCLUDED_PHONES_LABEL') }}
          </label>
          <p class="text-xs text-n-slate-10">
            {{ t('CONTACTS_ENGAGEMENT.EXCLUDED_PHONES_HELP') }}
          </p>
          <textarea
            v-model="phonesText"
            rows="10"
            :placeholder="t('CONTACTS_ENGAGEMENT.EXCLUDED_PHONES_PLACEHOLDER')"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand resize-y font-mono"
          />
          <p class="text-xs text-n-slate-10">
            {{
              t('CONTACTS_ENGAGEMENT.PHONES_COUNT', {
                count: phonesCount,
              })
            }}
          </p>
        </div>
      </div>

      <div v-if="result" class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
        <p class="text-sm font-semibold text-n-slate-12 mb-2">
          {{ t('CONTACTS_ENGAGEMENT.RESULT_TITLE') }}
        </p>
        <ul class="flex flex-col gap-1 text-sm text-n-slate-11">
          <li class="flex items-center gap-2">
            <span class="size-2 rounded-full bg-green-500 shrink-0" />
            <span class="font-mono text-xs">{{ result.engaged_label }}</span>
          </li>
          <li class="flex items-center gap-2">
            <span class="size-2 rounded-full bg-red-500 shrink-0" />
            <span class="font-mono text-xs">{{ result.not_engaged_label }}</span>
          </li>
        </ul>
        <p class="mt-2 text-xs text-n-slate-10">
          {{ t('CONTACTS_ENGAGEMENT.RESULT_NOTE') }}
        </p>
      </div>

      <div>
        <button
          :disabled="isLoading"
          class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-60 hover:bg-n-brand/90 focus:outline-none focus:ring-2 focus:ring-n-brand"
          @click="applyLabels"
        >
          <span
            v-if="isLoading"
            class="size-4 animate-spin rounded-full border-2 border-white border-t-transparent"
          />
          <span>{{ t('CONTACTS_ENGAGEMENT.APPLY_BUTTON') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
